import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

// Helper function to get wait event descriptions
function _getWaitEventDescription(waitEventType: string | null, waitEvent: string | null): string {
  if (!waitEventType) return 'No wait event';
  
  const descriptions: Record<string, string> = {
    'Client': 'Waiting for data from client application. Normal for idle connections.',
    'ClientRead': 'Waiting for client to send data. Connection is idle and ready.',
    'ClientWrite': 'Waiting to send data to client.',
    'Lock': 'Waiting for a database lock (table, row, etc.). May indicate contention.',
    'LWLock': 'Waiting for a lightweight lock. Usually brief.',
    'IO': 'Waiting for disk I/O operation.',
    'BufferPin': 'Waiting for buffer pin. Usually brief.',
    'Activity': 'Waiting for background activity.',
    'Extension': 'Waiting for extension activity.',
    'IPC': 'Inter-process communication wait.',
    'Timeout': 'Waiting for a timeout.',
  };
  
  if (waitEventType === 'Client' && waitEvent === 'ClientRead') {
    return 'PostgreSQL is waiting for the client application to send a new query. This is normal for idle connections in the pool.';
  }
  
  return descriptions[waitEventType] || descriptions[waitEvent || ''] || `Waiting for ${waitEventType}${waitEvent ? `: ${waitEvent}` : ''}`;
}

interface PoolStatus {
  pool_size: number;
  checked_in: number;
  checked_out: number;
  overflow: number;
  total_connections: number;
  available: number;
  in_use: number;
  utilization_pct: number;
  status: 'healthy' | 'warning' | 'exhausted';
  timestamp: string;
  pool_config?: {
    pool_size: number;
    max_overflow: number;
    timeout: number;
    recycle: number;
  };
}

interface HealthStatus {
  status: 'healthy' | 'unhealthy';
  timestamp: string;
  version: string;
  environment: string;
  database: {
    status: string;
    responsive: boolean;
  };
  pool: {
    status: string;
    in_use: number;
    total: number;
    utilization_pct: number;
  };
}

interface QueryLoggingStatus {
  enabled: boolean;
  echo_pool: boolean;
  instructions: {
    enable: string;
    disable: string;
    note: string;
    temporary: string;
  };
  warning: string;
}

interface ActiveConnection {
  pid: number;
  username: string;
  database: string;
  application_name: string;
  backend_type: string;
  client_addr: string | null;
  client_port: number | null;
  state: string;
  wait_event_type: string | null;
  wait_event: string | null;
  duration_seconds: number;
  duration_formatted: string;
  query_duration_seconds: number | null;
  query_duration_formatted: string | null;
  transaction_duration_seconds: number | null;
  transaction_duration_formatted: string | null;
  connection_duration_seconds: number | null;
  connection_duration_formatted: string | null;
  query: string | null;
  query_preview: string | null;
  is_transaction_control?: boolean;
  related_query_info?: {
    note: string;
    query_preview: string;
    calls?: number;
    avg_time_ms?: number;
  } | null;
  query_start: string | null;
  xact_start: string | null;
  backend_start: string | null;
  state_change: string | null;
  activity_summary: string;
}

interface ActiveConnectionsResponse {
  total_connections: number;
  active_queries: number;
  idle_connections: number;
  idle_in_transaction: number;
  waiting: number;
  connections: ActiveConnection[];
  timestamp: string;
  pg_stat_statements_available?: boolean;
  note: string;
}

interface DatabaseMonitoringProps {
  safeFetch?: (input: RequestInfo, init?: RequestInit) => Promise<Response>;
}

export const DatabaseMonitoring: React.FC<DatabaseMonitoringProps> = ({ safeFetch = fetch }) => {
  const [poolStatus, setPoolStatus] = useState<PoolStatus | null>(null);
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [queryLogging, setQueryLogging] = useState<QueryLoggingStatus | null>(null);
  const [activeConnections, setActiveConnections] = useState<ActiveConnectionsResponse | null>(null);
  const [showConnections, setShowConnections] = useState<boolean>(false);
  const [expandedPids, setExpandedPids] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [token, setToken] = useState<string | null>(null);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(true); // Assume authenticated initially

  // Get token and CSRF token from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem('dashboard_token');
    const storedCsrfToken = localStorage.getItem('dashboard_csrf_token');
    if (storedToken) {
      setToken(storedToken);
      setIsAuthenticated(true); // Assume valid if token exists
    } else {
      setIsAuthenticated(false); // No token = not authenticated
    }
    if (storedCsrfToken) {
      setCsrfToken(storedCsrfToken);
    }
  }, []);

  const getAuthHeaders = useCallback((method: string = 'GET') => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Add CSRF token for POST/PUT/DELETE requests (required by backend)
    if (csrfToken && (method === 'POST' || method === 'PUT' || method === 'DELETE' || method === 'PATCH')) {
      headers['X-CSRF-Token'] = csrfToken;
    }
    return headers;
  }, [token, csrfToken]);

  const fetchPoolStatus = useCallback(async () => {
    try {
      const response = await safeFetch(`${API_BASE}/api/v1/monitoring/pool-status`, {
        headers: getAuthHeaders(),
      });
      
      if (response.status === 401) {
        setIsAuthenticated(false);
        setError('Unauthorized: Please log in again');
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setPoolStatus(data);
        setError('');
        setIsAuthenticated(true);
      } else {
        const text = await response.text();
        setError(`Failed to fetch pool status: ${response.status} ${text}`);
      }
    } catch (e) {
      setError(`Error fetching pool status: ${e}`);
    }
  }, [getAuthHeaders, safeFetch]);

  const fetchHealthStatus = useCallback(async () => {
    try {
      const response = await safeFetch(`${API_BASE}/api/v1/monitoring/health`, {
        headers: getAuthHeaders(),
      });
      
      if (response.status === 401) {
        setIsAuthenticated(false);
        setError('Unauthorized: Please log in again');
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setHealthStatus(data);
        setError('');
        setIsAuthenticated(true);
      } else {
        const text = await response.text();
        setError(`Failed to fetch health status: ${response.status} ${text}`);
      }
    } catch (e) {
      setError(`Error fetching health status: ${e}`);
    }
  }, [getAuthHeaders, safeFetch]);

  const fetchQueryLoggingStatus = useCallback(async () => {
    try {
      const response = await safeFetch(`${API_BASE}/api/v1/monitoring/query-logging`, {
        headers: getAuthHeaders(),
      });
      
      if (response.status === 401) {
        setIsAuthenticated(false);
        setError('Unauthorized: Please log in again');
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setQueryLogging(data);
        setError('');
        setIsAuthenticated(true);
      } else {
        // Query logging requires admin permissions - might fail for non-admin users
        console.log('Query logging status not available (admin only)');
      }
    } catch (e) {
      console.log(`Query logging not available: ${e}`);
    }
  }, [getAuthHeaders, safeFetch]);

  const fetchActiveConnections = useCallback(async () => {
    try {
      const response = await safeFetch(`${API_BASE}/api/v1/monitoring/active-connections?limit=50`, {
        headers: getAuthHeaders(),
      });
      
      if (response.status === 401) {
        setIsAuthenticated(false);
        setError('Unauthorized: Please log in again');
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setActiveConnections(data);
        setError('');
        setIsAuthenticated(true);
      } else {
        // Active connections requires admin permissions
        console.log('Active connections not available (admin only)');
      }
    } catch (e) {
      console.log(`Active connections not available: ${e}`);
    }
  }, [getAuthHeaders, safeFetch]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const promises = [
      fetchPoolStatus(),
      fetchHealthStatus(),
      fetchQueryLoggingStatus(),
    ];
    // Only fetch active connections if the section is visible
    if (showConnections) {
      promises.push(fetchActiveConnections());
    }
    await Promise.all(promises);
    setLastRefresh(new Date());
    setLoading(false);
  }, [fetchPoolStatus, fetchHealthStatus, fetchQueryLoggingStatus, fetchActiveConnections, showConnections]);

  const toggleQueryLogging = async (enable: boolean) => {
    setLoading(true);
    try {
      const endpoint = enable ? 'enable' : 'disable';
      const response = await safeFetch(`${API_BASE}/api/v1/monitoring/query-logging/${endpoint}`, {
        method: 'POST',
        headers: getAuthHeaders('POST'),
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(data.message || `Query logging ${enable ? 'enabled' : 'disabled'}`);
        await fetchQueryLoggingStatus();
      } else {
        const text = await response.text();
        alert(`Failed: ${text}`);
      }
    } catch (e) {
      alert(`Error: ${e}`);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch and auto-refresh
  useEffect(() => {
    if (token && isAuthenticated) {
      fetchAll();
    }
  }, [token, isAuthenticated, fetchAll]);

  useEffect(() => {
    // Stop polling if not authenticated or no token
    if (!autoRefresh || !token || !isAuthenticated) return;

    const interval = setInterval(() => {
      fetchAll();
      // Also refresh active connections if visible
      if (showConnections) {
        fetchActiveConnections();
      }
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, token, isAuthenticated, showConnections, fetchAll, fetchActiveConnections]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'connected':
        return '#28a745';
      case 'warning':
        return '#ff9800';
      case 'exhausted':
      case 'unhealthy':
      case 'error':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  const getUtilizationColor = (pct: number) => {
    if (pct < 70) return '#28a745';
    if (pct < 90) return '#ff9800';
    return '#dc3545';
  };

  if (!token) {
    return (
      <div className="monitoring-section">
        <div className="section-title">Database & Connection Pool Monitoring</div>
        <div className="error-message">Authentication required. Please log in.</div>
      </div>
    );
  }

  return (
    <div className="monitoring-section">
      <div className="section-header">
        <div className="section-title">Database & Connection Pool Monitoring</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.9em' }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh (30s)
          </label>
          <button
            className={`refresh-button ${loading ? 'refreshing' : ''}`}
            onClick={fetchAll}
            disabled={loading}
            title="Refresh all metrics"
          >
            {loading ? '⏳' : '🔄'}
          </button>
        </div>
      </div>

      {lastRefresh && (
        <div style={{ fontSize: '0.85em', color: '#666', marginBottom: 12 }}>
          Last updated: {lastRefresh.toLocaleTimeString()}
        </div>
      )}

      {error && (
        <div className="error-message" style={{ marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* Health Status Overview */}
      {healthStatus && (
        <div style={{ marginBottom: 24 }}>
          <div className="section-title">System Health</div>
          <div className="stats-grid">
            <div className="stat-card">
              <div
                className="stat-number"
                style={{ color: getStatusColor(healthStatus.status) }}
              >
                {healthStatus.status.toUpperCase()}
              </div>
              <div className="stat-label">Overall Status</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{healthStatus.version}</div>
              <div className="stat-label">API Version</div>
            </div>
            <div className="stat-card">
              <div
                className="stat-number"
                style={{ color: getStatusColor(healthStatus.database.status) }}
              >
                {healthStatus.database.responsive ? '✓ Connected' : '✗ Disconnected'}
              </div>
              <div className="stat-label">Database</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{healthStatus.environment}</div>
              <div className="stat-label">Environment</div>
            </div>
          </div>
        </div>
      )}

      {/* Connection Pool Status */}
      {poolStatus && (
        <div style={{ marginBottom: 24 }}>
          <div className="section-title">Connection Pool Status</div>
          <div className="stats-grid">
            <div className="stat-card">
              <div
                className="stat-number"
                style={{ color: getStatusColor(poolStatus.status) }}
              >
                {poolStatus.status.toUpperCase()}
              </div>
              <div className="stat-label">Pool Status</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{poolStatus.in_use}</div>
              <div className="stat-label">Connections In Use</div>
              <div className="stat-detail">
                {poolStatus.checked_out} checked out
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{poolStatus.available}</div>
              <div className="stat-label">Available Connections</div>
              <div className="stat-detail">
                {poolStatus.checked_in} checked in
              </div>
            </div>
            <div className="stat-card">
              <div
                className="stat-number"
                style={{ color: getUtilizationColor(poolStatus.utilization_pct) }}
              >
                {poolStatus.utilization_pct.toFixed(1)}%
              </div>
              <div className="stat-label">Pool Utilization</div>
              <div className="stat-detail">
                {poolStatus.in_use} / {poolStatus.total_connections}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{poolStatus.overflow}</div>
              <div className="stat-label">Overflow Connections</div>
              <div className="stat-detail">
                Max: {poolStatus.pool_config?.max_overflow || 0}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{poolStatus.total_connections}</div>
              <div className="stat-label">Total Capacity</div>
              <div className="stat-detail">
                Base: {poolStatus.pool_config?.pool_size || 0}
              </div>
            </div>
          </div>

          {/* Pool Configuration Details */}
          {poolStatus.pool_config && (
            <div style={{ marginTop: 16 }}>
              <div className="endpoints-table">
                <div className="table-header">
                  <div>Configuration</div>
                  <div>Value</div>
                </div>
                <div className="table-row">
                  <div>Base Pool Size</div>
                  <div>{poolStatus.pool_config.pool_size} connections</div>
                </div>
                <div className="table-row">
                  <div>Max Overflow</div>
                  <div>{poolStatus.pool_config.max_overflow} connections</div>
                </div>
                <div className="table-row">
                  <div>Total Capacity</div>
                  <div>{poolStatus.total_connections} connections</div>
                </div>
                <div className="table-row">
                  <div>Connection Timeout</div>
                  <div>{poolStatus.pool_config.timeout}s</div>
                </div>
                <div className="table-row">
                  <div>Connection Recycle</div>
                  <div>{poolStatus.pool_config.recycle}s ({(poolStatus.pool_config.recycle / 60).toFixed(1)} min)</div>
                </div>
              </div>
            </div>
          )}

          {/* Status Interpretation */}
          <div style={{ marginTop: 16, padding: 16, background: '#f8f9fa', borderRadius: 8 }}>
            <strong>Status Guide:</strong>
            <ul style={{ marginTop: 8, marginBottom: 0 }}>
              <li><span style={{ color: '#28a745' }}>●</span> <strong>Healthy:</strong> &lt; 80% utilization - Normal operation</li>
              <li><span style={{ color: '#ff9800' }}>●</span> <strong>Warning:</strong> 80-99% utilization - Monitor closely</li>
              <li><span style={{ color: '#dc3545' }}>●</span> <strong>Exhausted:</strong> 100% utilization - Critical! Requests may timeout</li>
            </ul>
          </div>

          {/* Active Connections Toggle */}
          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              className="view-workflow-btn"
              onClick={() => {
                const newShow = !showConnections;
                setShowConnections(newShow);
                if (newShow) {
                  fetchActiveConnections();
                }
              }}
              style={{
                background: showConnections ? '#6c757d' : '#1e88e5',
                minWidth: 180,
              }}
            >
              {showConnections ? '▼ Hide Active Connections' : '▶ Show Active Connections'}
            </button>
            {poolStatus && poolStatus.in_use > 0 && (
              <span style={{ fontSize: '0.9em', color: '#666' }}>
                ({poolStatus.in_use} connection{poolStatus.in_use !== 1 ? 's' : ''} in use)
              </span>
            )}
          </div>
        </div>
      )}

      {/* Active Connections Details */}
      {showConnections && activeConnections && (
        <div style={{ marginBottom: 24 }}>
          <div className="section-header">
            <div className="section-title">Active Database Connections</div>
            <button
              className={`refresh-button ${loading ? 'refreshing' : ''}`}
              onClick={fetchActiveConnections}
              disabled={loading}
              title="Refresh active connections"
            >
              {loading ? '⏳' : '🔄'}
            </button>
          </div>
          
          {/* Summary Stats */}
          <div className="stats-grid" style={{ marginBottom: 16 }}>
            <div className="stat-card">
              <div className="stat-number">{activeConnections.total_connections}</div>
              <div className="stat-label">Total Connections</div>
            </div>
            <div className="stat-card">
              <div className="stat-number" style={{ color: '#dc3545' }}>
                {activeConnections.active_queries}
              </div>
              <div className="stat-label">Active Queries</div>
            </div>
            <div className="stat-card">
              <div className="stat-number" style={{ color: '#6c757d' }}>
                {activeConnections.idle_connections}
              </div>
              <div className="stat-label">Idle</div>
            </div>
            <div className="stat-card">
              <div className="stat-number" style={{ color: '#ff9800' }}>
                {activeConnections.idle_in_transaction}
              </div>
              <div className="stat-label">Idle in Transaction</div>
            </div>
            <div className="stat-card">
              <div className="stat-number" style={{ color: '#ff9800' }}>
                {activeConnections.waiting}
              </div>
              <div className="stat-label">Waiting</div>
              {activeConnections.waiting > 0 && (
                <div className="stat-detail" style={{ fontSize: '0.75em', marginTop: 4 }}>
                  {(() => {
                    const waitEvents = activeConnections.connections
                      .filter(c => c.wait_event_type)
                      .reduce((acc, c) => {
                        const key = `${c.wait_event_type}:${c.wait_event || 'none'}`;
                        acc[key] = (acc[key] || 0) + 1;
                        return acc;
                      }, {} as Record<string, number>);
                    const topWait = Object.entries(waitEvents)
                      .sort((a, b) => b[1] - a[1])[0];
                    return topWait ? `${topWait[0].split(':')[0]}: ${topWait[1]}` : 'See details';
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* Wait Events Summary */}
          {activeConnections.waiting > 0 && (
            <div style={{ marginBottom: 16, padding: 12, background: '#fff3cd', borderRadius: 8, border: '1px solid #ffc107' }}>
              <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: '0.9em' }}>
                ⏳ Wait Events Breakdown ({activeConnections.waiting} connections waiting):
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 8, fontSize: '0.85em' }}>
                {(() => {
                  const waitEvents = activeConnections.connections
                    .filter(c => c.wait_event_type)
                    .reduce((acc, c) => {
                      const key = `${c.wait_event_type}:${c.wait_event || 'none'}`;
                      if (!acc[key]) {
                        acc[key] = {
                          count: 0,
                          type: c.wait_event_type,
                          event: c.wait_event,
                          pids: [] as number[]
                        };
                      }
                      acc[key].count++;
                      acc[key].pids.push(c.pid);
                      return acc;
                    }, {} as Record<string, { count: number; type: string | null; event: string | null; pids: number[] }>);
                  
                  return Object.entries(waitEvents)
                    .sort((a, b) => b[1].count - a[1].count)
                    .map(([key, info]) => (
                      <div key={key} style={{ padding: 8, background: '#fff', borderRadius: 4, border: '1px solid #dee2e6' }}>
                        <div style={{ fontWeight: 'bold', color: '#856404' }}>
                          {info.type || 'Unknown'}
                          {info.event && `: ${info.event}`}
                        </div>
                        <div style={{ color: '#666', marginTop: 4 }}>
                          {info.count} connection{info.count !== 1 ? 's' : ''}
                        </div>
                        <div style={{ fontSize: '0.75em', color: '#999', marginTop: 4 }}>
                          {_getWaitEventDescription(info.type, info.event)}
                        </div>
                      </div>
                    ));
                })()}
              </div>
            </div>
          )}

          {/* Connections Table */}
          {activeConnections.connections.length > 0 ? (
            <div className="endpoints-table">
              <div className="table-header">
                <div style={{ minWidth: 60 }}>PID</div>
                <div style={{ minWidth: 120 }}>State</div>
                <div style={{ minWidth: 100 }}>Activity</div>
                <div style={{ minWidth: 100 }}>Query Time</div>
                <div style={{ minWidth: 100 }}>Txn Time</div>
                <div style={{ minWidth: 100 }}>Conn Time</div>
                <div style={{ minWidth: 150 }}>Application</div>
                <div style={{ minWidth: 100 }}>User</div>
                <div style={{ minWidth: 120 }}>Client</div>
                <div style={{ minWidth: 100 }}>Wait Event</div>
                <div style={{ flex: 1 }}>Query Preview</div>
              </div>
              {activeConnections.connections.map((conn, idx) => {
                const expanded = expandedPids.has(conn.pid);
                const toggleExpanded = () => {
                  setExpandedPids(prev => {
                    const next = new Set(prev);
                    if (next.has(conn.pid)) {
                      next.delete(conn.pid);
                    } else {
                      next.add(conn.pid);
                    }
                    return next;
                  });
                };
                return (
                  <React.Fragment key={idx}>
                    <div className="table-row" style={{ cursor: conn.query ? 'pointer' : 'default' }} onClick={conn.query ? toggleExpanded : undefined}>
                      <div>{conn.pid}</div>
                      <div>
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: 4,
                            fontSize: '0.85em',
                            background:
                              conn.state === 'active'
                                ? '#dc3545'
                                : conn.state === 'idle in transaction'
                                ? '#ff9800'
                                : '#6c757d',
                            color: 'white',
                          }}
                        >
                          {conn.state}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.85em', color: '#666' }}>
                        {conn.activity_summary || '-'}
                      </div>
                      <div>
                        {conn.query_duration_formatted ? (
                          <span
                            style={{
                              color:
                                (conn.query_duration_seconds || 0) > 60
                                  ? '#dc3545'
                                  : (conn.query_duration_seconds || 0) > 10
                                  ? '#ff9800'
                                  : '#28a745',
                              fontWeight: (conn.query_duration_seconds || 0) > 10 ? 'bold' : 'normal',
                            }}
                          >
                            {conn.query_duration_formatted}
                          </span>
                        ) : (
                          <span style={{ color: '#999' }}>-</span>
                        )}
                      </div>
                      <div>
                        {conn.transaction_duration_formatted ? (
                          <span
                            style={{
                              color:
                                (conn.transaction_duration_seconds || 0) > 60
                                  ? '#dc3545'
                                  : (conn.transaction_duration_seconds || 0) > 10
                                  ? '#ff9800'
                                  : '#6c757d',
                            }}
                          >
                            {conn.transaction_duration_formatted}
                          </span>
                        ) : (
                          <span style={{ color: '#999' }}>-</span>
                        )}
                      </div>
                      <div>
                        {conn.connection_duration_formatted ? (
                          <span style={{ color: '#6c757d', fontSize: '0.9em' }}>
                            {conn.connection_duration_formatted}
                          </span>
                        ) : (
                          <span style={{ color: '#999' }}>-</span>
                        )}
                      </div>
                      <div className="message-preview" title={conn.application_name || conn.backend_type}>
                        {conn.application_name || conn.backend_type || 'unknown'}
                      </div>
                      <div>{conn.username}</div>
                      <div style={{ fontSize: '0.85em' }}>
                        {conn.client_addr ? (
                          <span title={`${conn.client_addr}:${conn.client_port || ''}`}>
                            {conn.client_addr}
                            {conn.client_port && `:${conn.client_port}`}
                          </span>
                        ) : (
                          <span style={{ color: '#999' }}>-</span>
                        )}
                      </div>
                      <div>
                        {conn.wait_event_type ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span 
                              style={{ 
                                color: conn.wait_event_type === 'Client' ? '#6c757d' : '#ff9800', 
                                fontSize: '0.85em',
                                fontWeight: 'bold'
                              }}
                              title={_getWaitEventDescription(conn.wait_event_type, conn.wait_event)}
                            >
                              {conn.wait_event_type}
                              {conn.wait_event && `: ${conn.wait_event}`}
                            </span>
                            <span 
                              style={{ 
                                fontSize: '0.7em', 
                                color: '#999',
                                fontStyle: 'italic'
                              }}
                              title={_getWaitEventDescription(conn.wait_event_type, conn.wait_event)}
                            >
                              {_getWaitEventDescription(conn.wait_event_type, conn.wait_event).substring(0, 50)}
                              {_getWaitEventDescription(conn.wait_event_type, conn.wait_event).length > 50 ? '...' : ''}
                            </span>
                          </div>
                        ) : (
                          <span style={{ color: '#6c757d' }}>-</span>
                        )}
                      </div>
                      <div className="message-preview" title={conn.query_preview || conn.query || ''}>
                        {conn.query_preview || conn.query ? (
                          <>
                            {conn.is_transaction_control && (
                              <span style={{ 
                                fontSize: '0.75em', 
                                color: '#ff9800', 
                                fontStyle: 'italic',
                                display: 'block',
                                marginBottom: 4
                              }}>
                                ⚠️ Transaction control ({conn.query}) - see related query below
                              </span>
                            )}
                            <code style={{ fontSize: '0.85em' }}>
                              {(conn.query_preview || conn.query || '').length > 80
                                ? `${(conn.query_preview || conn.query || '').substring(0, 80)}...`
                                : (conn.query_preview || conn.query || '')}
                            </code>
                          </>
                        ) : (
                          <span style={{ color: '#999', fontStyle: 'italic' }}>No query</span>
                        )}
                        {conn.query && conn.query.length > 80 && (
                          <span style={{ marginLeft: 8, color: '#1e88e5', fontSize: '0.8em' }}>
                            {expanded ? '▼' : '▶'} {expanded ? 'Less' : 'More'}
                          </span>
                        )}
                      </div>
                    </div>
                    {expanded && conn.query && (
                      <div style={{ padding: '12px 16px', background: '#f8f9fa', borderTop: '1px solid #dee2e6' }}>
                        {conn.is_transaction_control && conn.related_query_info && (
                          <div style={{ 
                            marginBottom: 16, 
                            padding: 12, 
                            background: '#fff3cd', 
                            border: '1px solid #ffc107',
                            borderRadius: 4
                          }}>
                            <div style={{ fontWeight: 'bold', fontSize: '0.9em', marginBottom: 8, color: '#856404' }}>
                              📋 Related Query (from pg_stat_statements):
                            </div>
                            <div style={{ fontSize: '0.85em', color: '#666', marginBottom: 8 }}>
                              {conn.related_query_info.note}
                            </div>
                            <pre style={{ 
                              margin: 0, 
                              padding: 12, 
                              background: '#fff', 
                              border: '1px solid #dee2e6', 
                              borderRadius: 4,
                              overflow: 'auto',
                              maxHeight: '200px',
                              fontSize: '0.85em',
                              fontFamily: 'monospace'
                            }}>
                              {conn.related_query_info.query_preview}
                            </pre>
                            {conn.related_query_info.calls && (
                              <div style={{ marginTop: 8, fontSize: '0.85em', color: '#666' }}>
                                Executed {conn.related_query_info.calls} time(s)
                                {conn.related_query_info.avg_time_ms && 
                                  ` | Avg: ${conn.related_query_info.avg_time_ms}ms`}
                              </div>
                            )}
                          </div>
                        )}
                        {conn.is_transaction_control && !conn.related_query_info && (
                          <div style={{ 
                            marginBottom: 16, 
                            padding: 12, 
                            background: '#e7f3ff', 
                            border: '1px solid #1e88e5',
                            borderRadius: 4,
                            fontSize: '0.85em'
                          }}>
                            <strong>Note:</strong> This connection executed "{conn.query}". 
                            {activeConnections?.pg_stat_statements_available 
                              ? ' The actual query executed before this transaction control statement is not directly available from pg_stat_activity. Enable query logging to see full query history.'
                              : ' Enable pg_stat_statements extension to see query history.'}
                          </div>
                        )}
                        <div style={{ marginBottom: 8, fontWeight: 'bold', fontSize: '0.9em' }}>Current/Last Query:</div>
                        <pre style={{ 
                          margin: 0, 
                          padding: 12, 
                          background: '#fff', 
                          border: '1px solid #dee2e6', 
                          borderRadius: 4,
                          overflow: 'auto',
                          maxHeight: '300px',
                          fontSize: '0.85em',
                          fontFamily: 'monospace'
                        }}>
                          {conn.query}
                        </pre>
                        {conn.query_start && (
                          <div style={{ marginTop: 8, fontSize: '0.85em', color: '#666' }}>
                            Query started: {new Date(conn.query_start).toLocaleString()}
                          </div>
                        )}
                        {conn.xact_start && (
                          <div style={{ fontSize: '0.85em', color: '#666' }}>
                            Transaction started: {new Date(conn.xact_start).toLocaleString()}
                          </div>
                        )}
                        {conn.backend_start && (
                          <div style={{ fontSize: '0.85em', color: '#666' }}>
                            Connection started: {new Date(conn.backend_start).toLocaleString()}
                          </div>
                        )}
                        {conn.wait_event_type && (
                          <div style={{ marginTop: 12, padding: 12, background: '#e7f3ff', borderRadius: 4, border: '1px solid #1e88e5' }}>
                            <div style={{ fontWeight: 'bold', fontSize: '0.9em', marginBottom: 4 }}>
                              ⏳ Wait Event Details:
                            </div>
                            <div style={{ fontSize: '0.85em', color: '#666' }}>
                              <strong>Type:</strong> {conn.wait_event_type}
                              {conn.wait_event && (
                                <>
                                  <br />
                                  <strong>Event:</strong> {conn.wait_event}
                                </>
                              )}
                              <br />
                              <strong>Meaning:</strong> {_getWaitEventDescription(conn.wait_event_type, conn.wait_event)}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          ) : (
            <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
              No active connections found (excluding this monitoring query)
            </div>
          )}

          {activeConnections.note && (
            <div style={{ marginTop: 12, fontSize: '0.85em', color: '#666', fontStyle: 'italic' }}>
              {activeConnections.note}
            </div>
          )}

          {/* Column Help */}
          <div style={{ marginTop: 16, padding: 12, background: '#e7f3ff', borderRadius: 8, fontSize: '0.85em' }}>
            <strong>Column Guide:</strong>
            <ul style={{ marginTop: 4, marginBottom: 0 }}>
              <li><strong>PID:</strong> PostgreSQL process ID (can use to kill stuck queries: <code>SELECT pg_terminate_backend(pid)</code>)</li>
              <li><strong>State:</strong> Connection state - <span style={{color: '#dc3545'}}>active</span> (running query), <span style={{color: '#ff9800'}}>idle in transaction</span> (transaction open, potential leak), <span style={{color: '#6c757d'}}>idle</span> (waiting)</li>
              <li><strong>Query Time:</strong> How long current query has been running (red if &gt;60s)</li>
              <li><strong>Txn Time:</strong> How long transaction has been open (orange if &gt;10s - potential leak)</li>
              <li><strong>Conn Time:</strong> How long connection has been established</li>
              <li><strong>Client:</strong> IP address and port of client connection</li>
              <li><strong>Wait Event:</strong> What the connection is waiting for (if any)</li>
              <li><strong>Query Preview:</strong> Click row to expand and see full query text</li>
            </ul>
          </div>
        </div>
      )}

      {/* Query Logging Control */}
      {queryLogging && (
        <div style={{ marginBottom: 24 }}>
          <div className="section-title">Query Logging Control (Admin Only)</div>
          <div style={{ padding: 16, background: '#f8f9fa', borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
              <div>
                <strong>Current Status:</strong>
                <span
                  style={{
                    marginLeft: 8,
                    padding: '4px 12px',
                    borderRadius: 4,
                    background: queryLogging.enabled ? '#28a745' : '#6c757d',
                    color: 'white',
                    fontSize: '0.9em',
                  }}
                >
                  {queryLogging.enabled ? 'ENABLED' : 'DISABLED'}
                </span>
              </div>
              <button
                className="view-workflow-btn"
                onClick={() => toggleQueryLogging(!queryLogging.enabled)}
                disabled={loading}
                style={{
                  background: queryLogging.enabled ? '#dc3545' : '#28a745',
                  minWidth: 120,
                }}
              >
                {queryLogging.enabled ? 'Disable Logging' : 'Enable Logging'}
              </button>
            </div>

            <div style={{ marginTop: 12, padding: 12, background: '#fff3cd', borderRadius: 4 }}>
              <strong>⚠️ Warning:</strong> {queryLogging.warning}
            </div>

            <div style={{ marginTop: 12, fontSize: '0.9em', color: '#666' }}>
              <strong>Instructions:</strong>
              <ul style={{ marginTop: 4, marginBottom: 0 }}>
                <li>{queryLogging.instructions.note}</li>
                <li>{queryLogging.instructions.temporary}</li>
              </ul>
            </div>

            <div style={{ marginTop: 12, fontSize: '0.85em', color: '#666', fontStyle: 'italic' }}>
              To view logged queries, check Docker logs:
              <code style={{ display: 'block', marginTop: 4, padding: 8, background: '#e9ecef', borderRadius: 4 }}>
                sudo docker compose logs -f api | grep "sqlalchemy.engine"
              </code>
            </div>
          </div>
        </div>
      )}

      {/* Usage Guide */}
      <div style={{ marginTop: 24 }}>
        <div className="section-title">Monitoring Guide</div>
        <div style={{ padding: 16, background: '#f8f9fa', borderRadius: 8 }}>
          <h4 style={{ marginTop: 0 }}>What to Monitor:</h4>
          <ul>
            <li><strong>Pool Utilization:</strong> Should stay below 80% under normal load</li>
            <li><strong>Available Connections:</strong> Should never drop to 0</li>
            <li><strong>Overflow Usage:</strong> Occasional spikes are OK, sustained usage indicates need for more capacity</li>
            <li><strong>Database Responsive:</strong> Should always be true</li>
          </ul>

          <h4>Troubleshooting:</h4>
          <ul>
            <li><strong>High utilization (80-100%):</strong>
              <ul>
                <li>Enable query logging to identify slow queries</li>
                <li>Check for connection leaks (connections not being closed)</li>
                <li>Review recent code changes</li>
                <li>Consider increasing pool size temporarily</li>
              </ul>
            </li>
            <li><strong>Database not responsive:</strong>
              <ul>
                <li>Check RDS status in AWS Console</li>
                <li>Review database CPU/memory metrics</li>
                <li>Check for long-running queries</li>
              </ul>
            </li>
            <li><strong>Pool exhausted:</strong>
              <ul>
                <li>URGENT: Restart API if users are seeing errors</li>
                <li>Enable query logging immediately after restart</li>
                <li>Identify and fix slow queries</li>
                <li>Check background worker processes</li>
              </ul>
            </li>
          </ul>

          <h4>Best Practices:</h4>
          <ul>
            <li>Monitor utilization during peak hours</li>
            <li>Enable query logging only when debugging (impacts performance)</li>
            <li>Keep auto-refresh enabled for real-time monitoring</li>
            <li>Set up alerts for utilization &gt; 80%</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DatabaseMonitoring;

